import axios from 'axios';
import type { AuthTokenService } from './AuthTokenService';
import { captureException } from '@sentry/nextjs';

export interface PartnerSessionCreateRequest {
  user_msisdn: string;
  campaign_id: string;
  source: string;
  user_data: {
    first_name: string;
    last_name: string;
    gender: string;
    date_of_birth: string;
  };
  request_id: string;
}

export interface PartnerSessionCreateResponse {
  session_token: string;
  redirect_url: string;
  expires_at: string;
  partner_code: string;
  campaign_id: string;
}

export interface PartnerSessionValidateResponse {
  missing_fields: string[];
  user_status: 'new_user' | 'existing_user';
  auto_login_available: boolean;
  requires_otp: boolean;
  requires_otp_reason?: string;
  user_data: {
    first_name?: string;
    last_name?: string;
    msisdn?: string;
    gender?: string;
    date_of_birth?: string;
  };
  partner_code: string;
  campaign_id: string;
  expires_at: string;
}

export interface PartnerRegisterRequest {
  session_token: string;
  additional_data: {
    email?: string;
    region?: string;
    employment_status?: string;
    whatsapp_same_number?: boolean;
  };
  terms_acceptance: {
    terms_version: string;
    privacy_version: string;
    marketing_consent: boolean;
  };
  device_info: {
    device_id: string;
    platform: string;
    user_agent: string;
  };
}

export interface PartnerLoginRequest {
  session_token: string;
  device_info: {
    device_id: string;
    platform: string;
    user_agent: string;
  };
}

export interface PartnerAuthResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  access?: string,
  refresh?: string,
  user: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string | null;
  };
  kyc_level?: string;
  registration_status: string;
  partner?: string;
  campaign_id?: string;
  authentication_method: string;
  partner_code?: string,
  is_new_user?: boolean,
}

export class PartnerAuthService {
  private readonly apiClient;

  constructor(
    private readonly authTokenService: AuthTokenService,
    private readonly baseUrl: string
  ) {
    this.apiClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token when available
    this.apiClient.interceptors.request.use(
      (config) => {
        const url = typeof config.url === 'string' ? config.url : '';
        const isAuthEndpoint = url.startsWith('/v1/auth/');
        if (!isAuthEndpoint) {
          const token = this.authTokenService.getAuthToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor to handle token refresh
    this.apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const url = typeof originalRequest?.url === 'string' ? originalRequest.url : '';
        const shouldAttemptRefresh =
          url !== '' && !url.startsWith('/v1/auth/') && !url.startsWith('/v1/partner/session/');
        if (error.response?.status === 401 && shouldAttemptRefresh && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const newToken = await this.authTokenService.refreshToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.apiClient(originalRequest);
          } catch (refreshError) {
            this.authTokenService.clearAuthToken();
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async createPartnerSession(
    request: PartnerSessionCreateRequest,
    apiKey: string
  ): Promise<PartnerSessionCreateResponse> {
    const response = await this.apiClient.post(
      '/v1/partner/session/create',
      {
        user_msisdn: request.user_msisdn,
        campaign_id: request.campaign_id,
        source: request.source,
        user_data: request.user_data,
        request_id: request.request_id,
      },
      {
        headers: {
          'X-API-Key': apiKey,
        },
      }
    );

    return {
      session_token: response.data.session_token,
      redirect_url: response.data.redirect_url,
      expires_at: response.data.expires_at,
      partner_code: response.data.partner_code,
      campaign_id: response.data.campaign_id,
    };
  }

  async validatePartnerSession(
    sessionToken: string
  ): Promise<PartnerSessionValidateResponse> {
    const response = await this.apiClient.get(
      `/v1/auth/partner/validate?token=${sessionToken}`
    );

    return response.data as Promise<PartnerSessionValidateResponse>;
  }

  async registerPartnerUser(
    request: PartnerRegisterRequest
  ): Promise<PartnerAuthResponse> {
    const response = await this.apiClient.post('/v1/auth/partner/register', {
      session_token: request.session_token,
      additional_data: request.additional_data,
      terms_acceptance: request.terms_acceptance,
      device_info: request.device_info,
    });

    const authResponse: PartnerAuthResponse = {
      access_token: response.data.access_token,
      refresh_token: response.data.refresh_token,
      expires_in: response.data.expires_in,
      token_type: response.data.token_type,
      user: response.data.user,
      kyc_level: response.data.kyc_level,
      registration_status: response.data.registration_status,
      partner: response.data.partner,
      campaign_id: response.data.campaign_id,
      authentication_method: 'PARTNER',
    };

    // Store tokens in auth service
    this.authTokenService.setAuthToken(authResponse.access_token);

    return authResponse;
  }

  async loginPartnerUser(
    request: PartnerLoginRequest
  ): Promise<PartnerAuthResponse> {
    const response = await this.apiClient.post('/v1/auth/partner/login', {
      session_token: request.session_token,
      device_info: request.device_info,
    });

    const authResponse: PartnerAuthResponse = {
      access_token: response.data.access_token,
      refresh_token: response.data.refresh_token,
      expires_in: response.data.expires_in,
      token_type: response.data.token_type,
      user: response.data.user,
      registration_status: response.data.registration_status,
      authentication_method: 'PARTNER',
    };

    // Store tokens in auth service
    this.authTokenService.setAuthToken(authResponse.access_token);

    return authResponse;
  }

  parsePartnerToken(url: string): string | null {
    try {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('token');
    } catch (error) {
      captureException(error);
      return null;
    }
  }

  generateDeviceInfo(): any {
    return {
      device_id: this.generateDeviceId(),
      platform: this.detectPlatform(),
      user_agent:
        typeof window !== 'undefined' ? window.navigator.userAgent : 'Server',
    };
  }

  generateIdempotencyKey(): string {
    return `partner_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateDeviceId(): string {
    // Try to get existing device ID from localStorage
    if (typeof window !== 'undefined') {
      let deviceId = localStorage.getItem('motito_device_id');
      if (!deviceId) {
        deviceId = `device_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;
        localStorage.setItem('motito_device_id', deviceId);
      }
      return deviceId;
    }
    return `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private detectPlatform(): string {
    if (typeof window === 'undefined') return 'server';

    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.includes('mobile')) return 'mobile';
    if (userAgent.includes('tablet')) return 'tablet';
    return 'web';
  }
}
